import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Dashboard.css';
import axios from 'axios';
import { API_BASE_URL } from '../../constants';
import { useAuth } from '../../AuthContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { IoChevronUp, IoChevronDown } from "react-icons/io5";
import { limitFileName } from '../../helpers/limitFileName';

interface MessageItem {
    id: number;
    absenderId: string;
    betreff: string;
    text: string;
    versandart: string;
    empfaenger: string;
    versanddatum: string;
    status: string;
    nachzuforderndeDokumente?: string;
}

interface DownloadableDocument {
    guid: string;
    description?: string;
    name?: string;
}

const Korrespondenz: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const antragId = location.state?.antragId;
    const { user } = useAuth();
    const [messages, setMessages] = React.useState<MessageItem[]>([]);

    const [newSubject, setNewSubject] = React.useState('');
    const [newText, setNewText] = React.useState('');
    const [sending, setSending] = React.useState(false);
    const [showNewMessageForm, setShowNewMessageForm] = React.useState(false);
    const [showReplyForm, setShowReplyForm] = React.useState(false);
    const [replySubject, setReplySubject] = React.useState('');
    const [replyText, setReplyText] = React.useState('');
    const [selectedMessage, setSelectedMessage] = React.useState<MessageItem | null>(null);
    const [showMessageDetail, setShowMessageDetail] = React.useState(false);

    const [selectedFiles, setSelectedFiles] = React.useState<(File | null)[]>([]);
    const [uploadingIndex, setUploadingIndex] = React.useState<number | null>(null);

    const [questions, setQuestions] = React.useState<{
        arbeitInNoe: boolean | null;
        geplanteArbeitInNoe: boolean | null;
        wohnsitzInNoe: boolean | null;
    }>({
        arbeitInNoe: null,
        geplanteArbeitInNoe: null,
        wohnsitzInNoe: null,
    });

    const [downloadedDocuments, setDownloadedDocuments] = React.useState<Set<string>>(new Set());
    const allQuestionsAnswered = Object.values(questions).every(value => value !== null);
    const [downloadableDocs, setDownloadableDocs] = React.useState<DownloadableDocument[]>([]);
    const [downloadingDocTypes, setDownloadingDocTypes] = React.useState<Set<string>>(new Set());

    const AntwortDisabled = sending || !replySubject || !replyText || (!!selectedMessage?.nachzuforderndeDokumente &&
        selectedMessage.nachzuforderndeDokumente.split(';')
            .filter(doc => {
                const trimmedDoc = doc.trim().toLowerCase();
                return trimmedDoc !== 'gutachten' && trimmedDoc !== 'bescheid';
            })
            .some((_, index) => !selectedFiles[index]));

    const isDisabled = !newSubject || !newText || sending;

    React.useEffect(() => {
        if (!antragId) {
            navigate('/dashboard');
        }
    }, [antragId, navigate]);

    const formatGermanDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('de-DE');
    };

    const sendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        setSending(true);
        const sendPromise = axios.post(`${API_BASE_URL}/application/message`, {
            absenderId: user?.email,
            betreff: newSubject,
            text: newText,
            versandart: 'inapp',
            empfaenger: 'nostrifizierung@imc.ac.at',
            versanddatum: new Date().toISOString(),
            status: 'Gesendet',
            antragId: antragId
        }, {
            headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
        });

        toast.promise(
            sendPromise,
            {
                pending: 'Nachricht wird gesendet...',
                success: 'Nachricht erfolgreich gesendet!',
                error: 'Fehler beim Senden der Nachricht. Bitte versuchen Sie es erneut.'
            }
        ).then(() => {
            setNewSubject('');
            setNewText('');
            getMessages();
        }).catch(() => {
            console.error('Error sending message');
        }).finally(() => {
            setSending(false);
        });
    };

    const getMessages = async () => {
        try {
            const response = await axios.get<MessageItem[]>(`${API_BASE_URL}/application/message/${antragId}`, {
                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
            });
            console.log('Fetched messages:', response.data);
            setMessages(response.data);
        }
        catch (error) {
            console.error('Error fetching messages:', error);
        }
    };

    const confirmMessage = async (messageId: number) => {
        try {
            await axios.put(`${API_BASE_URL}/application/message/${messageId}/confirm`, {}, {
                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
            });
        } catch (error) {
            console.error('Error confirming message:', error);
        }
    }

    const messagesRef = React.useRef<MessageItem[]>(messages);
    React.useEffect(() => { messagesRef.current = messages; }, [messages]);

    const attemptedConfirmRef = React.useRef<Set<number>>(new Set());

    React.useEffect(() => {
        const timer = setTimeout(() => {
            const msgs = messagesRef.current || [];
            msgs.forEach(msg => {
                if (msg.status === 'Gesendet' && msg.absenderId !== user?.email && !attemptedConfirmRef.current.has(msg.id)) {
                    attemptedConfirmRef.current.add(msg.id);
                    confirmMessage(msg.id);
                }
            });
        }, 10000);

        return () => clearTimeout(timer);
    }, [user?.email]);

    React.useEffect(() => {
        getMessages();
        getGutachtenundBescheid();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleMessageClick = (msg: MessageItem) => {
        setSelectedMessage(msg);
        setShowMessageDetail(true);
        setShowReplyForm(false);
        setSelectedFiles([]);
        setReplySubject('');
        setReplyText('');

    };

    const handleReplyClick = () => {
        if (selectedMessage) {
            setShowReplyForm(true);
            setTimeout(() => {
                const replyForm = document.getElementById('reply-form');
                if (replyForm) replyForm.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    };

    const sendReply = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!replySubject || !replyText || !selectedMessage || !antragId) return;

        setSending(true);
        const sendPromise = axios.post(`${API_BASE_URL}/application/message`, {
            absenderId: user?.email,
            betreff: replySubject,
            text: replyText,
            versandart: 'inapp',
            empfaenger: 'nostrifizierung@imc.ac.at',
            versanddatum: new Date().toISOString(),
            status: 'Gesendet',
            antragId: antragId
        }, {
            headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
        });

        toast.promise(
            sendPromise,
            {
                pending: 'Antwort wird gesendet...',
                success: 'Antwort erfolgreich gesendet!',
                error: 'Fehler beim Senden der Antwort. Bitte versuchen Sie es erneut.'
            }
        ).then(() => {
            setReplySubject('');
            setReplyText('');
            setShowReplyForm(false);
            setSelectedMessage(null);
            setShowMessageDetail(false);
            setSelectedFiles([]);
            getMessages();
        }).catch(() => {
            console.error('Error sending reply');
        }).finally(() => {
            setSending(false);
        });
    };

    const handleFileChange = (index: number, file: File | null) => {
        const newFiles = [...selectedFiles];
        newFiles[index] = file;
        setSelectedFiles(newFiles);
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        if (!e.target.files || !antragId) {
            if (!antragId) {
                toast.error('Bitte speichern Sie zuerst den Antrag');
            }
            return;
        }

        const files = Array.from(e.target.files);
        setUploadingIndex(index);

        for (const file of files) {
            try {
                const reader = new FileReader();

                reader.onload = async () => {
                    try {
                        const base64String = (reader.result as string).split(',')[1];
                        const uploadFileName = limitFileName(file.name);


                        const uploadDocs = selectedMessage?.nachzuforderndeDokumente?.split(';')
                            .filter(doc => {
                                const trimmedDoc = doc.trim().toLowerCase();
                                return trimmedDoc !== 'gutachten' && trimmedDoc !== 'bescheid';
                            }) || [];

                        const documentUploadItem = {
                            fileName: uploadFileName,
                            mimeType: file.type,
                            binaryData: base64String,
                            description: uploadDocs[index]?.trim() || ''
                        };

                        const url = `${API_BASE_URL}/application/document?antragId=${antragId}`;

                        const response = await axios.post(url, documentUploadItem, {
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${sessionStorage.getItem('token')}`
                            }
                        });

                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const { guid, fileName } = response.data;

                        const newFiles = [...selectedFiles];
                        newFiles[index] = file;
                        setSelectedFiles(newFiles);

                        setUploadingIndex(null);
                        toast.success(`${uploadFileName || file.name} erfolgreich hochgeladen`);
                    } catch (error) {
                        console.error(`Error uploading file ${file.name}:`, error);
                        toast.error(`Fehler beim Hochladen von ${file.name}`);
                        setUploadingIndex(null);
                    }
                };

                reader.onerror = (error) => {
                    console.error("Error reading file:", error);
                    toast.error(`Fehler beim Lesen von ${file.name}`);
                    setUploadingIndex(null);
                };

                reader.readAsDataURL(file);
            } catch (error) {
                console.error(`Error processing file ${file.name}:`, error);
                setUploadingIndex(null);
            }
        }

        e.target.value = '';
    };

    const getGutachtenundBescheid = async () => {
        try {
            const response = await axios.get<DownloadableDocument[]>(`${API_BASE_URL}/application/gutachtenUndBescheid/${antragId}`, {
                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
            });
            console.log('Fetched messages:', response.data);
            setDownloadableDocs(response.data);
        }
        catch (error) {
            console.error('Error fetching messages:', error);
        }
    }

    const downloadDocumentByType = async (docType: string) => {
        // normalize the incoming label to match downloadableDocs entries
        const normalized = docType.trim().toLowerCase();

        if (downloadingDocTypes.has(normalized)) return;

        setDownloadingDocTypes(prev => {
            const next = new Set(prev);
            next.add(normalized);
            return next;
        });

        // try to find a matching document entry
        const entry = downloadableDocs.find((d: DownloadableDocument) => {
            const t = (d.description || '').toString().trim().toLowerCase();
            const name = (d.name || '').toString().trim().toLowerCase();
            return t === normalized || name.includes(normalized);
        });

        if (!entry || !(entry.guid)) {
            console.error(`No downloadable document found for type: ${docType}`);
            return;
        }

        const guid = entry.guid;
        const suggestedName =
            (entry.name || `${normalized}.pdf`).toString();

        try {
            const response = await axios.get(
                `${API_BASE_URL}/application/document/download/${guid}`,
                { responseType: 'blob' }
            );

            if (!response.data || response.data.size === 0) {
                console.error('Empty blob received');
                return;
            }

            const contentType =
                response.headers['content-type'] || 'application/octet-stream';
            const blob = new Blob([response.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = suggestedName;
            document.body.appendChild(a);
            a.click();
            a.remove();

            setTimeout(() => window.URL.revokeObjectURL(url), 10000);

            const newDownloadedDocs = new Set(downloadedDocuments);
            const isFirstDownloadForType = !newDownloadedDocs.has(normalized);
            newDownloadedDocs.add(normalized);
            setDownloadedDocuments(newDownloadedDocs);

            if (isFirstDownloadForType) {
                await createDocumentDownloadNotification(normalized);

                if (normalized === 'gutachten') {
                    await handleAntragStatusChange('Einspruchsfrist läuft');
                }

                if (selectedMessage?.id) {
                    await setEmpfangsdatumAfterDownload(selectedMessage.id);
                }

                if (normalized === 'bescheid') {
                    await postAnsweredQuestions(questions);
                }
            }

        } catch (error) {
            console.error('Error downloading document:', error);
        } finally {
            setDownloadingDocTypes(prev => {
                const next = new Set(prev);
                next.delete(normalized);
                return next;
            });
        }
    };

    const handleAntragStatusChange = async (status: string) => {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/admin/change-antrag-status/${antragId}`,
                JSON.stringify(status),
                { headers: { "Content-Type": "application/json" } }
            );
            console.log('Antrag status changed successfully:', response.data);
        } catch (error) {
            console.error('Failed to change Antrag status:', error);
        }
    }

    const setEmpfangsdatumAfterDownload = async (messageId: number) => {
        try {
            await axios.put(
                `${API_BASE_URL}/application/message/${messageId}/empfangsdatum`,
                {},
                { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
            );
        } catch (error) {
            console.error('Failed to set Empfangsdatum after download:', error);
        }
    };

    const createDocumentDownloadNotification = async (downloadedDocType: string) => {
        try {

            const docLabel =
                downloadedDocType === 'gutachten'
                    ? 'Gutachten'
                    : downloadedDocType === 'bescheid'
                        ? 'Bescheid'
                        : downloadedDocType;

            const article = downloadedDocType === 'bescheid' ? 'den' : 'das';

            const notificationMessage = `Der Benutzer hat ${article} ${docLabel} heruntergeladen.`;

            await axios.post(`${API_BASE_URL}/application/message`, {
                absenderId: user?.email,
                betreff: `${docLabel} heruntergeladen`,
                text: notificationMessage,
                versandart: 'inapp',
                empfaenger: 'nostrifizierung@imc.ac.at',
                versanddatum: new Date().toISOString(),
                status: 'Gesendet',
                antragId: antragId,
                Intern: true
            }, {
                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
            });

        } catch (error) {
            console.error('Error creating notification message:', error);
        }
    };

    const postAnsweredQuestions = async (questionsData: {
        arbeitInNoe: boolean | null;
        geplanteArbeitInNoe: boolean | null;
        wohnsitzInNoe: boolean | null;
    }) => {
        try {
            await axios.post(`${API_BASE_URL}/application/questions?antragId=${antragId}`, questionsData, {
                headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
            });
            console.log('Questions posted successfully');
        } catch (error) {
            console.error('Failed to post questions:', error);
        }
    };

    return (
        <div className='db-container'>
            <div className='usermenu'>
                <button
                    className='logout'
                    type='button'
                    onClick={() => window.history.back()}
                    style={{ width: '100%' }}
                >
                    Zurück zum Dashboard
                </button>
            </div>
            <div className="dashboard-container" id="dashboard">
                <h1 className="dashboard-heading">
                    Korrespondenz
                </h1>
                <div
                    style={{
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                        padding: '24px',
                        width: '90%',
                        margin: 'auto',
                    }}
                >
                    <h2 style={{ fontSize: '1.2em', marginBottom: '32px' }}>Nachrichtenverlauf</h2>
                    {messages.length === 0 ? (
                        <div style={{ textAlign: 'center', color: '#888', padding: '24px', fontSize: '1.1em' }}>
                            Noch keine Nachrichten vorhanden. Senden Sie eine Nachricht, wenn sie Fragen haben oder Unterstützung benötigen.
                        </div>
                    ) : (
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            background: '#fff',
                            fontSize: '1em'
                        }}>
                            <thead>
                                <tr style={{ background: '#f3f4f7' }}>
                                    <th style={{ padding: '12px', borderBottom: '2px solid #c1c1c1', textAlign: 'center', fontSize: '1.1em' }}>Betreff</th>
                                    <th style={{ padding: '12px', borderBottom: '2px solid #c1c1c1', textAlign: 'center', fontSize: '1.1em' }}>Absender</th>
                                    <th style={{ padding: '12px', borderBottom: '2px solid #c1c1c1', textAlign: 'center', fontSize: '1.1em' }}>Empfänger</th>
                                    <th style={{ padding: '12px', borderBottom: '2px solid #c1c1c1', textAlign: 'center', fontSize: '1.1em' }}>Datum</th>
                                </tr>
                            </thead>
                            <tbody>
                                {messages.map(msg => (
                                    <tr
                                        key={msg.id}
                                        onClick={() => handleMessageClick(msg)}
                                        style={{
                                            borderBottom: '1px solid #eee',
                                            cursor: 'pointer',
                                            backgroundColor: selectedMessage?.id === msg.id ? '#f5f5f5ff' : 'transparent',
                                            transition: 'background-color 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (selectedMessage?.id !== msg.id) {
                                                e.currentTarget.style.backgroundColor = '#f5f5f5ff';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (selectedMessage?.id !== msg.id) {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                            }
                                        }}
                                    >
                                        <td style={{ padding: '12px', fontWeight: 'bold', verticalAlign: 'top' }}>{msg.betreff}</td>
                                        <td style={{ padding: '12px', verticalAlign: 'top' }}>{msg.absenderId}</td>
                                        <td style={{ padding: '12px', verticalAlign: 'top' }}>{msg.empfaenger}</td>
                                        <td style={{ padding: '12px', verticalAlign: 'top', color: '#888' }}>{formatGermanDate(msg.versanddatum)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {/* Message Detail View */}
                    {showMessageDetail && selectedMessage && (
                        <div id="message-detail" style={{ marginTop: '32px', background: '#f9f9f9', padding: '24px', borderRadius: '8px', border: '2px solid #ff9e1b' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                                <div style={{ flex: 1 }}>
                                    <h3 style={{ fontSize: '1.3em', marginBottom: '16px', color: '#333' }}>{selectedMessage.betreff}</h3>
                                    <div style={{ marginBottom: '16px' }}>
                                        <p style={{ margin: '4px 0', fontSize: '0.95em' }}>
                                            <span style={{ fontWeight: 600 }}>Von:</span> {selectedMessage.absenderId}
                                        </p>
                                        <p style={{ margin: '4px 0', fontSize: '0.95em' }}>
                                            <span style={{ fontWeight: 600 }}>An:</span> {selectedMessage.empfaenger}
                                        </p>
                                        <p style={{ margin: '4px 0', fontSize: '0.95em' }}>
                                            <span style={{ fontWeight: 600 }}>Datum:</span> {formatGermanDate(selectedMessage.versanddatum)}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowMessageDetail(false);
                                        setSelectedMessage(null);
                                        setShowReplyForm(false);
                                        setSelectedFiles([]);
                                        setDownloadedDocuments(new Set());
                                        setQuestions({
                                            arbeitInNoe: null,
                                            geplanteArbeitInNoe: null,
                                            wohnsitzInNoe: null,
                                        });
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '1.5em',
                                        cursor: 'pointer',
                                        color: '#666',
                                        padding: '0 8px'
                                    }}
                                    title="Schließen"
                                >
                                    ×
                                </button>
                            </div>

                            <div style={{
                                background: '#fff',
                                padding: '16px',
                                borderRadius: '4px',
                                marginBottom: '24px',
                                minHeight: '100px',
                                whiteSpace: 'pre-wrap'
                            }}>
                                {selectedMessage.text}
                            </div>

                            {selectedMessage.nachzuforderndeDokumente && selectedMessage.nachzuforderndeDokumente?.split(';')
                                .filter(doc => {
                                    const trimmedDoc = doc.trim().toLowerCase();
                                    return trimmedDoc !== 'gutachten' && trimmedDoc !== 'bescheid';
                                }).length > 0 && (
                                    <div style={{
                                        //background: '#fff',
                                        //padding: '16px',
                                        minHeight: '100px',
                                        whiteSpace: 'pre-wrap',
                                        fontStyle: 'italic',
                                    }}>
                                        Um die nachgeforderten Dokumente hochzuladen, antworten Sie bitte auf diese Nachricht und laden Sie das korrekte Dokument bei dem entsprechenden Typ hoch.
                                    </div>
                                )}

                            {/* NÖ-Fragen */}
                            {selectedMessage.nachzuforderndeDokumente && selectedMessage.nachzuforderndeDokumente?.split(';')
                                .filter(doc => {
                                    const trimmedDoc = doc.trim().toLowerCase();
                                    return trimmedDoc === 'bescheid';
                                }).length > 0 && (
                                    <div style={{ marginBottom: '24px', background: '#fff', padding: '16px', borderRadius: '4px', border: '1px solid #ddd' }}>
                                        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <label style={{ fontSize: '0.95em' }}>Sind oder waren Sie in Niederösterreich beruflich tätig?</label>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setQuestions({ ...questions, arbeitInNoe: true })}
                                                    style={{
                                                        padding: '6px 12px',
                                                        background: questions.arbeitInNoe === true ? 'rgb(255, 158, 27)' : '#f0f0f0',
                                                        color: questions.arbeitInNoe === true ? '#fff' : '#333',
                                                        border: '1px solid #c1c1c1',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    Ja
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setQuestions({ ...questions, arbeitInNoe: false })}
                                                    style={{
                                                        padding: '6px 12px',
                                                        background: questions.arbeitInNoe === false ? 'rgb(255, 158, 27)' : '#f0f0f0',
                                                        color: questions.arbeitInNoe === false ? '#fff' : '#333',
                                                        border: '1px solid #c1c1c1',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    Nein
                                                </button>
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <label style={{ fontSize: '0.95em' }}>Planen Sie in Niederösterreich beruflich tätig zu werden?</label>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setQuestions({ ...questions, geplanteArbeitInNoe: true })}
                                                    style={{
                                                        padding: '6px 12px',
                                                        background: questions.geplanteArbeitInNoe === true ? 'rgb(255, 158, 27)' : '#f0f0f0',
                                                        color: questions.geplanteArbeitInNoe === true ? '#fff' : '#333',
                                                        border: '1px solid #c1c1c1',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    Ja
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setQuestions({ ...questions, geplanteArbeitInNoe: false })}
                                                    style={{
                                                        padding: '6px 12px',
                                                        background: questions.geplanteArbeitInNoe === false ? 'rgb(255, 158, 27)' : '#f0f0f0',
                                                        color: questions.geplanteArbeitInNoe === false ? '#fff' : '#333',
                                                        border: '1px solid #c1c1c1',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    Nein
                                                </button>
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <label style={{ fontSize: '0.95em' }}>Haben oder hatten Sie einen Wohnsitz in Niederösterreich?</label>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setQuestions({ ...questions, wohnsitzInNoe: true })}
                                                    style={{
                                                        padding: '6px 12px',
                                                        background: questions.wohnsitzInNoe === true ? 'rgb(255, 158, 27)' : '#f0f0f0',
                                                        color: questions.wohnsitzInNoe === true ? '#fff' : '#333',
                                                        border: '1px solid #c1c1c1',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    Ja
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setQuestions({ ...questions, wohnsitzInNoe: false })}
                                                    style={{
                                                        padding: '6px 12px',
                                                        background: questions.wohnsitzInNoe === false ? 'rgb(255, 158, 27)' : '#f0f0f0',
                                                        color: questions.wohnsitzInNoe === false ? '#fff' : '#333',
                                                        border: '1px solid #c1c1c1',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    Nein
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                            {/* Download buttons for Gutachten and Bescheid */}
                            {selectedMessage.nachzuforderndeDokumente?.split(';')
                                .filter(doc => {
                                    const trimmedDoc = doc.trim().toLowerCase();
                                    return trimmedDoc === 'gutachten' || trimmedDoc === 'bescheid';
                                })
                                .map((doc, index) => {
                                    const normalizedDoc = doc.trim().toLowerCase();
                                    const requiresQuestions = normalizedDoc === 'bescheid';
                                    const isDownloading = downloadingDocTypes.has(normalizedDoc);
                                    const isDisabled = isDownloading || (requiresQuestions && !allQuestionsAnswered);

                                    return (
                                        <div key={`download-${index}`} style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                                            <span style={{ minWidth: '200px', flex: '0 0 200px' }}>{doc.trim()}</span>
                                            <button
                                                type="button"
                                                disabled={isDisabled}
                                                onClick={() => !isDisabled && downloadDocumentByType(doc)}
                                                style={{
                                                    display: 'inline-block',
                                                    padding: '8px 12px',
                                                    backgroundColor: isDisabled ? '#e8e8e8' : '#f0f0f0',
                                                    color: isDisabled ? '#999' : '#333',
                                                    border: '1px solid #c1c1c1',
                                                    borderRadius: '4px',
                                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                    fontSize: '14px',
                                                    textAlign: 'center',
                                                    transition: 'background-color 0.2s',
                                                    minWidth: '150px',
                                                    opacity: isDisabled ? 0.6 : 1
                                                }}
                                                onMouseEnter={(e) => !isDisabled && (e.currentTarget.style.backgroundColor = '#e0e0e0')}
                                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isDisabled ? '#e8e8e8' : '#f0f0f0')}
                                            >
                                                <i className="bi bi-download" style={{ marginRight: '8px' }}></i>
                                                {isDownloading ? 'Wird heruntergeladen...' : 'Herunterladen'}
                                            </button>
                                        </div>
                                    );
                                })}

                            <button
                                onClick={handleReplyClick}
                                style={{
                                    padding: '10px 20px',
                                    background: 'rgb(255, 158, 27)',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '1em',
                                    fontWeight: 600,
                                    marginTop: '16px'
                                }}
                            >
                                <i className="bi bi-reply" style={{ marginRight: '8px' }}></i>
                                Antworten
                            </button>
                        </div>
                    )}

                    {/* Reply Form */}
                    {showReplyForm && selectedMessage && (
                        <form id="reply-form" onSubmit={sendReply} style={{ marginTop: '24px', background: '#fff', padding: '24px', borderRadius: '8px', border: '1px solid #ddd' }}>
                            <h3 style={{ fontSize: '1.1em', marginBottom: '16px' }}>Antwort auf: {selectedMessage.betreff}</h3>

                            {/* Upload buttons for other documents */}
                            {selectedMessage.nachzuforderndeDokumente?.split(';')
                                .filter(doc => {
                                    const trimmedDoc = doc.trim().toLowerCase();
                                    return trimmedDoc !== 'gutachten' && trimmedDoc !== 'bescheid';
                                })
                                .map((doc, index) => (
                                    <div key={index} style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                                        <span style={{ minWidth: '200px', flex: '0 0 200px' }}>{doc.trim()}</span>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                id={`file-input-${index}`}
                                                type="file"
                                                style={{ display: 'none' }}
                                                accept='.pdf,.png,.jpg'
                                                multiple={false}
                                                onChange={(e) => {
                                                    handleFileChange(index, e.target.files?.[0] || null);
                                                    handleFileUpload(e, index);
                                                }}
                                            />
                                            <label
                                                htmlFor={`file-input-${index}`}
                                                style={{
                                                    display: 'inline-block',
                                                    padding: '8px 12px',
                                                    backgroundColor: '#f0f0f0',
                                                    color: '#333',
                                                    border: '1px solid #c1c1c1',
                                                    borderRadius: '4px',
                                                    cursor: uploadingIndex === index ? 'not-allowed' : 'pointer',
                                                    fontSize: '14px',
                                                    textAlign: 'center',
                                                    transition: 'background-color 0.2s',
                                                    minWidth: '150px',
                                                    marginBottom: 0,
                                                    opacity: uploadingIndex === index ? 0.7 : selectedFiles[index] ? 0.8 : 1,
                                                    pointerEvents: selectedFiles[index] ? 'none' : 'auto'
                                                }}
                                                onMouseEnter={(e) => uploadingIndex !== index && (e.currentTarget.style.backgroundColor = '#e0e0e0')}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                            >
                                                {uploadingIndex === index ? (
                                                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                        <span className="spinner-border spinner-border-sm" role="status" style={{ width: '16px', height: '16px' }}>
                                                            <span className="sr-only">Loading...</span>
                                                        </span>
                                                        Wird hochgeladen...
                                                    </span>
                                                ) : selectedFiles[index] ? (
                                                    selectedFiles[index]!.name
                                                ) : (
                                                    'Datei auswählen'
                                                )}
                                            </label>
                                        </div>
                                    </div>
                                ))}
                            <div style={{ marginBottom: '12px' }}>
                                <label style={{ display: 'block', textAlign: 'left', fontSize: '16px', fontWeight: 600 }}>Betreff:</label>
                                <input
                                    type="text"
                                    value={replySubject}
                                    onChange={e => setReplySubject(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        marginTop: '4px',
                                        border: '1px solid #c1c1c1',
                                        borderRadius: '5px'
                                    }}
                                />
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                                <label style={{ display: 'block', textAlign: 'left', fontSize: '16px', fontWeight: 600 }}>Nachricht:</label>
                                <textarea
                                    value={replyText}
                                    onChange={e => setReplyText(e.target.value)}
                                    rows={4}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        marginTop: '4px',
                                        resize: 'vertical',
                                        height: '120px',
                                        border: '1px solid #c1c1c1',
                                        borderRadius: '5px'
                                    }}
                                />
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    type="submit"
                                    disabled={AntwortDisabled}
                                    style={{
                                        padding: '8px 16px',
                                        background: AntwortDisabled ? '#ccc' : 'rgb(255, 158, 27)',
                                        color: AntwortDisabled ? '#888' : '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: AntwortDisabled ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {sending ? 'Wird gesendet...' : 'Antwort senden'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowReplyForm(false)}
                                    style={{
                                        padding: '8px 16px',
                                        background: '#ccc',
                                        color: '#333',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Abbrechen
                                </button>
                            </div>
                        </form>
                    )}
                </div>
                <div style={{ margin: '32px auto', width: '90%' }}>
                    <button
                        onClick={() => setShowNewMessageForm(!showNewMessageForm)}
                        style={{
                            width: '100%',
                            padding: '12px',
                            background: 'rgb(255, 158, 27)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '1.1em',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}
                    >
                        Neue Nachricht verfassen
                        {showNewMessageForm ? <IoChevronUp /> : <IoChevronDown />}
                    </button>
                    {showNewMessageForm && (
                        <form onSubmit={sendMessage} style={{ marginTop: '16px', background: '#f9f9f9', padding: '24px', borderRadius: '8px' }}>
                            <h2 style={{ fontSize: '1.2em', marginBottom: '32px' }}>Neue Nachricht verfassen</h2>
                            <div style={{ marginBottom: '12px' }}>
                                <label style={{ display: 'block', textAlign: 'left', fontSize: '18px', fontWeight: 600 }}>Betreff:</label>
                                <input
                                    type="text"
                                    value={newSubject}
                                    onChange={e => setNewSubject(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        marginTop: '4px',
                                        border: '1px solid #c1c1c1',
                                        borderRadius: '5px'
                                    }}
                                />
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                                <label style={{ display: 'block', textAlign: 'left', fontSize: '18px', fontWeight: 600 }}>Nachricht:</label>
                                <textarea
                                    value={newText}
                                    onChange={e => setNewText(e.target.value)}
                                    rows={4}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        marginTop: '4px',
                                        resize: 'vertical',
                                        height: '160px',
                                        maxHeight: '400px',
                                        minHeight: '80px',
                                        border: '1px solid #c1c1c1',
                                        borderRadius: '5px'
                                    }}
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={isDisabled}
                                style={{
                                    padding: '8px 16px',
                                    background: isDisabled ? '#ccc' : 'rgb(255, 158, 27)',
                                    color: isDisabled ? '#888' : '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {sending ? 'Wird gesendet...' : 'Nachricht senden'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Korrespondenz;